import { KyOptions } from 'ky/distribution/types/options';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    FilterUIConfigType,
    RowDataSourceTableNumeric,
} from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import {
    ApiGridParams,
    ColumnDefinition,
    DatasetFilter,
    ExportExcelArgs,
    DownloadFileResult,
    FilterUIArgs,
    FilterUICategoryConfig,
    FilterUIStateUpdate,
    FilterUIValue,
    GetStudyDataSetsParams,
    CM2Dataset,
    InfiniteScrollGridResponse,
    ObsActionsLogsCountDto,
    PagingRequest,
    PanelResult,
    RadialChartSiteDto,
    StudyDashboardPanelData,
    StudySiteDto,
    Widget,
    FilterUICalculateFilters,
    PreAppliedFilters,
    RadialChartStudyDto,
    AbortablePagingRequst,
} from '@interfaces/central-monitoring';
import {
    formConfigUrl,
    formDataUrl,
    formExportUrl,
    formIndexUrl,
} from '@apis/bff-api/central-monitoring/api-grid-helpers';
import { getData, postData, postDataForFile, ValidationErrorResponse } from '@apis/rest-client';

export const useGetActiveStudyDataSets = ({ studyId }: GetStudyDataSetsParams, skip = false) => {
    const enabled = !skip && !!studyId;
    return useSWR<CM2Dataset[], ValidationErrorResponse>(
        enabled ? `CentralMonitoring/DataSet/active/${studyId}` : null,
        getData,
    );
};

export const useGetActiveDatasetsBySite = (studyRef: string, siteId: number | undefined) => {
    const url = siteId ? `CentralMonitoring/Datasets/${studyRef}/${siteId}` : null;
    return useSWR<CM2Dataset[], ValidationErrorResponse>(url, getData);
};

export const useGetStudyDashboardPanelsData = (
    studyRef: string,
    datasetId: number,
    previousDatasetCount: number,
    datasetFilter: DatasetFilter,
    siteFilterSessionId: string,
) => {
    return useSWRMutation<StudyDashboardPanelData[], ValidationErrorResponse>(
        `CentralMonitoring/Panels/${studyRef}/study-dashboard-panels-data/${datasetId}` +
            `?previousDatasetCount=${previousDatasetCount}` +
            `&datasetFilter=${datasetFilter}` +
            `&siteFilterSessionId=${siteFilterSessionId}`,
        getData,
        {
            rollbackOnError: false,
        },
    );
};

export const useGetDatasetSites = (studyRef: string, datasetId: number | undefined) => {
    const url = datasetId ? `CentralMonitoring/StudySites/${studyRef}/${datasetId}` : null;

    return useSWR<StudySiteDto[], ValidationErrorResponse>(url, getData);
};

export const useGetLatestDataSet = (studyRef: string) => {
    const url = `CentralMonitoring/dataset/latest/${studyRef}`;

    return useSWR<CM2Dataset, ValidationErrorResponse>(url, getData);
};

export const useGetWidgets = (
    studyRef: string,
    datasetId: number,
    siteFilterSessionId: string,
    comparisonDatasetId: number | null,
) => {
    let url = `CentralMonitoring/Widgets/${studyRef}/${datasetId}?siteFilterSessionId=${siteFilterSessionId}`;
    if (comparisonDatasetId != null) {
        url = `${url}&comparisonDatasetId=${comparisonDatasetId}`;
    }
    return useSWRMutation<Widget[], ValidationErrorResponse>(url, getData);
};

export const useGetFilterUIConfig = (
    studyRef: string,
    dataViewConfigId?: string,
    retryOnError?: boolean,
    sessionId?: string,
    type?: FilterUIConfigType,
    copySessionId?: string,
) => {
    let enabled = true;
    const queryParams = new URLSearchParams();
    let url: string | null = `CentralMonitoring/FilterUI/${studyRef}`;
    if (sessionId) queryParams.set('sessionId', sessionId);
    if (copySessionId) queryParams.set('copySessionId', copySessionId);
    if (dataViewConfigId) url = `${url}/${dataViewConfigId}`;
    else if (type === FilterUIConfigType.StudyDashboard) {
        queryParams.set('type', '2');
    } else if (type === FilterUIConfigType.SubjectMonitoringHub) {
        url = `SubjectMonitoring/FilterUI/${studyRef}`;
        queryParams.set('type', '3');
    } else {
        enabled = false;
    }

    if (queryParams.size) url = `${url}?${queryParams.toString()}`;

    return dataViewConfigId
        ? useSWRMutation<FilterUICategoryConfig, ValidationErrorResponse, string | null, PreAppliedFilters | null>(
              enabled ? url : null,
              (key: string, { arg }: { arg: PreAppliedFilters | null }) => {
                  return postData(key, { arg, timeout: 30000 });
              },
          )
        : useSWRMutation<FilterUICategoryConfig, ValidationErrorResponse, string | null, PreAppliedFilters | null>(
              enabled ? url : null,
              async (key: string, options?: KyOptions) => {
                  const opt: KyOptions = {
                      ...options,
                      timeout: 30000,
                  };
                  // let apiUrl = key;
                  // if (sessionId) apiUrl = `${apiUrl}?sessionId=${sessionId}`;
                  return getData(key, opt);
              },
              {
                  shouldRetryOnError: retryOnError,
              },
          );
};

export const useGetStudyHasDataset = (studyRef: string | null) =>
    useSWR<boolean, ValidationErrorResponse>(
        studyRef ? `CentralMonitoring/StudyHasDataset/${studyRef}` : null,
        getData,
    );

export const useGetFilterUIValues = (
    studyRef: string,
    sessionId: string,
    datasetId?: number,
    context?: RowDataSourceTableNumeric,
    fieldKey?: string,
    dataviewRef?: string,
) => {
    const url = `CentralMonitoring/FilterUI/${studyRef}/values`;
    const queryParams = new URLSearchParams();
    queryParams.set('sessionId', sessionId);
    if (datasetId !== undefined) queryParams.set('datasetId', datasetId.toString());
    if (context) queryParams.set('context', context.toString());
    if (fieldKey) queryParams.set('fieldKey', fieldKey);
    if (dataviewRef) queryParams.set('dataviewRef', dataviewRef);

    return useSWRMutation<Array<FilterUIValue>, ValidationErrorResponse, string | null, FilterUIStateUpdate>(
        !!context && !!fieldKey ? `${url}?${queryParams.toString()}` : null,
        (key: string, { arg }: { arg: FilterUIStateUpdate }) => {
            return postData(key, { arg, timeout: 30000 });
        },
    );
};

export const useApplyFilterUIValues = (
    studyRef: string,
    sessionId: string,
    datasetId?: number,
    dataViewRef?: string,
) => {
    const queryParams = new URLSearchParams();
    const baseUrl = `CentralMonitoring/FilterUI/${studyRef}/apply`;
    queryParams.set('sessionId', sessionId);
    if (datasetId !== undefined) queryParams.set('datasetId', datasetId.toString());
    if (dataViewRef) queryParams.set('dataViewRef', dataViewRef);

    return useSWRMutation<FilterUICalculateFilters, ValidationErrorResponse, string | null, FilterUIStateUpdate>(
        `${baseUrl}?${queryParams.toString()}`,
        (url: string, { arg }: { arg: FilterUIStateUpdate }) => {
            return postData(url, { arg, timeout: 30000 });
        },
    );
};

export const usePerformFilterUIAction = (
    studyRef: string,
    sessionId: string,
    datasetId?: number,
    dataviewRef?: string,
) => {
    const queryParams = new URLSearchParams();
    queryParams.set('sessionId', sessionId);
    if (datasetId !== undefined) queryParams.set('datasetId', datasetId.toString());
    if (dataviewRef) queryParams.set('dataviewRef', dataviewRef);

    const baseUrl = `CentralMonitoring/FilterUI/${studyRef}/action?${queryParams.toString()}`;
    return useSWRMutation<boolean, ValidationErrorResponse, string | null, FilterUIArgs>(
        !!sessionId ? baseUrl : null,
        (url: string, { arg }: { arg: FilterUIArgs }) => {
            return postData<FilterUIArgs, boolean>(url, { arg, timeout: 30000 });
        },
    );
};

export const useGetGridData = (gridParams: ApiGridParams) => {
    const key = formDataUrl(gridParams);
    return useSWRMutation<InfiniteScrollGridResponse, ValidationErrorResponse, string | null, AbortablePagingRequst>(
        key,
        (url: string, { arg }: { arg: AbortablePagingRequst }) => {
            const { signal, ...rest } = arg;
            return postData<PagingRequest, InfiniteScrollGridResponse>(url, {
                arg: rest,
                timeout: 30000,
                signal,
            });
        },
    );
};

export const useGetGridConfig = (gridParams: ApiGridParams) => {
    const url = formConfigUrl(gridParams);
    return useSWR<ColumnDefinition[], ValidationErrorResponse>(url, getData);
};

export const useGetGridIndex = (gridParams: ApiGridParams) => {
    const url = formIndexUrl(gridParams);
    return useSWRMutation<number, ValidationErrorResponse, string | null, PagingRequest, number>(url, postData);
};

export const useExportGridData = (gridParams: ApiGridParams) => {
    const urlStr = formExportUrl(gridParams);
    return useSWRMutation<DownloadFileResult, ValidationErrorResponse, string | null, ExportExcelArgs>(
        urlStr,
        async (url, data) => {
            const response = await postDataForFile(url, { ...data, timeout: 30000 });
            // Process response to get file name from headers
            const cdisp = response.headers.get('Content-Disposition');
            const reg: RegExp = /filename="(.*)"/;
            const match = cdisp?.match(reg);
            const fileName = match ? match[1] : null;
            const blob = await response.blob();
            // Return formed data to caller
            return { fileName, blob };
        },
    );
};

export const useGetPanelData = (
    studyRef: string,
    datasetId: number,
    panelRef: string,
    dataViewRef: string,
    sessionId?: string,
) => {
    let url = `CentralMonitoring/Panel-data/${studyRef}/${datasetId}/${panelRef}/${dataViewRef}`;
    if (sessionId) url += `?sessionId=${sessionId}`;

    return useSWRMutation<PanelResult, ValidationErrorResponse, string | null>(
        url,
        (urlString: string) => {
            return getData(urlString, { timeout: 30000, retry: { limit: 10, statusCodes: [503], delay: () => 1000 } });
        },
        {
            rollbackOnError: false,
        },
    );
};
export const useGetObsActionsLogsCount = (studyRef: string, panelRef: string | undefined) => {
    const url = panelRef ? `CentralMonitoring/Observation/${studyRef}/GetObsActionsLogsCount/${panelRef}` : null;

    return useSWR<ObsActionsLogsCountDto, ValidationErrorResponse>(url, getData);
};

export const useGetPanelPreview = (studyRef: string) => {
    return useSWRMutation(`CentralMonitoring/panel-preview/${studyRef}`, (url: string, { arg }: { arg: string }) => {
        return postData<string, PanelResult>(url, { arg, timeout: 30000 });
    });
};

export const useGetRadialChartSiteData = (
    studyRef: string,
    selectedSiteId: number | undefined,
    datasetId: number | undefined,
) => {
    const url =
        selectedSiteId && datasetId
            ? `CentralMonitoring/RadialCharts/${studyRef}/${selectedSiteId}/${datasetId}`
            : null;

    return useSWR<RadialChartSiteDto, ValidationErrorResponse>(url, getData);
};

export const useGetRadialChartStudyData = (studyRef: string, datasetId: number | undefined) => {
    const url = datasetId ? `CentralMonitoring/RadialCharts/${studyRef}/${datasetId}` : null;

    return useSWR<RadialChartStudyDto, ValidationErrorResponse>(url, getData);
};

export const useGetCountriesForStudy = (studyRef: string) => {
    const url = studyRef ? `CentralMonitoring/Study/${studyRef}/countries` : null;

    return useSWR<string[], ValidationErrorResponse>(url, getData);
};
